import {
    PDFArray, PDFDict, PDFDocument, PDFName, PDFRawStream, PDFRef, decodePDFRawStream,
} from 'pdf-lib';
import { contentStreamsOf, isDelim, isWhite, tokenize } from '@/features/pdf-editor/engine/stripText';

/**
 * Finding — and taking out — the watermark a PDF already carries.
 *
 * There is no "watermark" object in the PDF format: a watermark is only a
 * drawing convention, so it has to be recognised by how it behaves rather than
 * by what it is called. Four shapes cover nearly everything produced in the
 * wild, and each is recognised by evidence rather than by a name alone:
 *
 *   optional content  a marked-content span tied to an OCG called "watermark"
 *   form XObject      one form drawn on (nearly) every page, faint or turned
 *   text run          the same short string on (nearly) every page, faint or turned
 *   annotation        a /Watermark — and sometimes /Stamp — annotation
 *
 * "Faint or turned" is what keeps a page number or a running header out of the
 * list: a real watermark is drawn under the content with an alpha below 1, or
 * across it at an angle, and almost always both.
 *
 * Removal rewrites the content streams, dropping only the operators that draw
 * the chosen watermark and copying everything else across byte for byte, the
 * way the editor's text strip does. A page that cannot be parsed with
 * confidence is left exactly as it was — a watermark that survives beats a
 * corrupted file.
 */

/** A candidate has to appear on at least this share of the pages to be a watermark. */
const PAGE_SHARE = 0.6;
/** Longer than this and it is a paragraph, not a stamp. */
const MAX_TEXT_LENGTH = 60;
/** Below this alpha the drawing is see-through — the giveaway of a watermark. */
const FAINT_ALPHA = 0.92;

const WATERMARK_WORD = /water\s*-?\s*mark|wasserzeichen|\bwm\b|ওয়াটারমার্ক/i;

const decoder = new TextDecoder('latin1');
const readToken = (bytes, token) => decoder.decode(bytes.subarray(token.start, token.end));

/** `/Name` → `Name`, with the #xx escapes PDF names may carry resolved. */
function nameOf(bytes, token) {
    const raw = readToken(bytes, token).slice(1);
    return raw.replace(/#([0-9a-f]{2})/gi, (_, hex) => String.fromCharCode(parseInt(hex, 16)));
}

/** The visible characters of a PDF string token, good enough to compare two runs. */
function stringOf(bytes, token) {
    const raw = readToken(bytes, token);
    if (raw.startsWith('<')) {
        const hex = raw.slice(1, -1).replace(/[^0-9a-f]/gi, '');
        let out = '';
        // A PDF text string is one byte per character unless it opens with the
        // UTF-16 byte-order mark. Reading two bytes per character regardless is
        // what turned "CONFIDENTIAL" into CJK on the first run of this.
        if (/^feff/i.test(hex)) {
            for (let i = 4; i + 3 < hex.length; i += 4) {
                const code = parseInt(hex.slice(i, i + 4), 16);
                if (code) out += String.fromCharCode(code);
            }
            return out;
        }
        for (let i = 0; i + 1 < hex.length; i += 2) {
            const code = parseInt(hex.slice(i, i + 2), 16);
            if (code) out += String.fromCharCode(code);
        }
        return out;
    }
    let out = '';
    for (let i = 1; i < raw.length - 1; i += 1) {
        const ch = raw[i];
        if (ch === '\\') { i += 1; out += raw[i] ?? ''; continue; }
        out += ch;
    }
    return out;
}

const normalise = (text) => text.replace(/\s+/g, ' ').trim();

/** The `/ca` (fill alpha) of every ExtGState the page can reach. */
function alphaByGraphicsState(page) {
    const out = new Map();
    try {
        const resources = page.node.Resources?.() || page.node.get(PDFName.of('Resources'));
        const states = page.node.context.lookup(resources?.get?.(PDFName.of('ExtGState')));
        if (!(states instanceof PDFDict)) return out;
        for (const [key, value] of states.entries()) {
            const dict = page.node.context.lookup(value);
            if (!(dict instanceof PDFDict)) continue;
            const ca = dict.get(PDFName.of('ca'));
            const alpha = typeof ca?.asNumber === 'function' ? ca.asNumber() : null;
            if (alpha != null) out.set(key.asString().slice(1), alpha);
        }
    } catch { /* an unreadable resource dict simply means no alpha hints */ }
    return out;
}

/**
 * A page's `/XObject` names mapped to the object they point at.
 *
 * The same stamp gets a DIFFERENT resource name on every page — producers
 * generate the key per page — so counting by name says "on one page" for a
 * watermark that is on all of them. The object reference is what is actually
 * shared, so that is what everything downstream counts by; the per-page name is
 * kept because removal has to match the name the page's own stream uses.
 */
function xobjectRefs(page) {
    const byName = new Map();
    try {
        const resources = page.node.context.lookup(
            page.node.Resources?.() || page.node.get(PDFName.of('Resources')),
        );
        const xobjects = page.node.context.lookup(resources?.get?.(PDFName.of('XObject')));
        if (!(xobjects instanceof PDFDict)) return byName;
        for (const [key, value] of xobjects.entries()) {
            const name = key.asString().slice(1);
            const refKey = value instanceof PDFRef
                ? `${value.objectNumber} ${value.generationNumber}`
                : `inline:${name}`;
            const dict = page.node.context.lookup(value);
            const subtype = dict?.dict?.get?.(PDFName.of('Subtype'))
                ?? dict?.get?.(PDFName.of('Subtype'));
            byName.set(name, {
                refKey,
                subtype: typeof subtype?.asString === 'function' ? subtype.asString().slice(1) : '',
                hasOc: !!(dict?.dict?.get?.(PDFName.of('OC')) ?? dict?.get?.(PDFName.of('OC'))),
            });
        }
    } catch { /* an unreadable resource dict just means nothing to match */ }
    return byName;
}

/** Names of the optional-content groups whose name says "watermark". */
function watermarkOcgNames(pdfDoc) {
    const flagged = new Set();
    try {
        const props = pdfDoc.catalog.get(PDFName.of('OCProperties'));
        const dict = pdfDoc.context.lookup(props);
        if (!(dict instanceof PDFDict)) return flagged;
        const groups = pdfDoc.context.lookup(dict.get(PDFName.of('OCGs')));
        if (!(groups instanceof PDFArray)) return flagged;
        for (let i = 0; i < groups.size(); i += 1) {
            const ref = groups.get(i);
            const ocg = pdfDoc.context.lookup(ref);
            if (!(ocg instanceof PDFDict)) continue;
            const label = ocg.get(PDFName.of('Name'));
            const text = typeof label?.decodeText === 'function' ? label.decodeText() : String(label ?? '');
            if (WATERMARK_WORD.test(text) && ref instanceof PDFRef) {
                flagged.add(`${ref.objectNumber} ${ref.generationNumber}`);
            }
        }
    } catch { /* no OCProperties: nothing to flag */ }
    return flagged;
}

/** Which `/OC /Name` entries of this page point at a flagged OCG. */
function flaggedPropertyNames(page, flaggedOcgs) {
    const names = new Set();
    try {
        const resources = page.node.Resources?.() || page.node.get(PDFName.of('Resources'));
        const properties = page.node.context.lookup(resources?.get?.(PDFName.of('Properties')));
        if (!(properties instanceof PDFDict)) return names;
        for (const [key, value] of properties.entries()) {
            if (value instanceof PDFRef && flaggedOcgs.has(`${value.objectNumber} ${value.generationNumber}`)) {
                names.add(key.asString().slice(1));
            }
        }
    } catch { /* ignore */ }
    return names;
}

/**
 * Walk one page's streams and report what was drawn: which forms and images
 * were painted, and which text runs were shown with what alpha and angle.
 */
function readPage(page, flaggedProperties) {
    const alphas = alphaByGraphicsState(page);
    const refs = xobjectRefs(page);
    const result = { xobjects: new Map(), texts: new Map(), ocSpans: 0, parsed: true };

    let streams;
    try {
        streams = contentStreamsOf(page);
    } catch {
        streams = null;
    }
    if (!streams) return { ...result, parsed: false };

    for (const { stream } of streams) {
        let bytes;
        try {
            bytes = decodePDFRawStream(stream).decode();
        } catch {
            return { ...result, parsed: false };
        }
        const tokens = tokenize(bytes);
        if (!tokens) return { ...result, parsed: false };

        let alpha = 1;
        let skew = 0;                 // |b| + |c| of the running matrix: non-zero = turned
        const stateStack = [];
        let rotated = false;
        let runAlpha = 1;
        let run = '';
        let inText = false;
        const operands = [];

        for (let i = 0; i < tokens.length; i += 1) {
            const token = tokens[i];
            if (token.type !== 'op') { operands.push(token); continue; }
            const op = token.value;
            const nums = operands.filter((tk) => tk.type === 'number').map((tk) => tk.value);
            const names = operands.filter((tk) => tk.type === 'name');
            const strings = operands.filter((tk) => tk.type === 'string');

            switch (op) {
                case 'q': stateStack.push([alpha, skew]); break;
                case 'Q': {
                    const prev = stateStack.pop();
                    alpha = prev ? prev[0] : 1;
                    skew = prev ? prev[1] : 0;
                    break;
                }
                case 'cm': {
                    if (nums.length >= 4) skew += Math.abs(nums[1]) + Math.abs(nums[2]);
                    break;
                }
                case 'gs': {
                    const key = names.length ? nameOf(bytes, names[names.length - 1]) : null;
                    if (key && alphas.has(key)) alpha = alphas.get(key);
                    break;
                }
                case 'BDC': {
                    const key = names.length > 1 ? nameOf(bytes, names[1]) : null;
                    if (key && flaggedProperties.has(key)) result.ocSpans += 1;
                    break;
                }
                case 'Do': {
                    const key = names.length ? nameOf(bytes, names[names.length - 1]) : null;
                    const entry = key ? refs.get(key) : null;
                    if (key) {
                        const id = entry?.refKey || `name:${key}`;
                        const seen = result.xobjects.get(id) || {
                            count: 0, alpha: 1, rotated: false, names: new Set(), subtype: entry?.subtype || '',
                        };
                        seen.names.add(key);
                        result.xobjects.set(id, {
                            count: seen.count + 1,
                            alpha: Math.min(seen.alpha, alpha),
                            rotated: seen.rotated || skew > 0.01,
                            names: seen.names,
                            subtype: seen.subtype || entry?.subtype || '',
                        });
                    }
                    break;
                }
                case 'BT': inText = true; rotated = false; run = ''; runAlpha = alpha; break;
                case 'Tm': {
                    // b and c are the off-diagonal terms: non-zero means the text
                    // is turned, which upright body text never is.
                    if (nums.length >= 4 && (Math.abs(nums[1]) > 0.01 || Math.abs(nums[2]) > 0.01)) rotated = true;
                    break;
                }
                case 'Tj': case "'": case '"': {
                    if (strings.length) run += stringOf(bytes, strings[strings.length - 1]);
                    runAlpha = Math.min(runAlpha, alpha);
                    break;
                }
                case 'TJ': {
                    for (const tk of strings) run += stringOf(bytes, tk);
                    runAlpha = Math.min(runAlpha, alpha);
                    break;
                }
                case 'ET': {
                    const text = normalise(run);
                    if (inText && text && text.length <= MAX_TEXT_LENGTH) {
                        const seen = result.texts.get(text) || { count: 0, alpha: 1, rotated: false };
                        result.texts.set(text, {
                            count: seen.count + 1,
                            alpha: Math.min(seen.alpha, runAlpha),
                            rotated: seen.rotated || rotated,
                        });
                    }
                    inText = false;
                    run = '';
                    break;
                }
                default: break;
            }
            operands.length = 0;
        }
    }

    return result;
}

/** Watermark-ish annotations on one page, as their index in /Annots. */
function watermarkAnnotations(page) {
    const found = [];
    try {
        const annots = page.node.context.lookup(page.node.get(PDFName.of('Annots')));
        if (!(annots instanceof PDFArray)) return found;
        for (let i = 0; i < annots.size(); i += 1) {
            const annot = page.node.context.lookup(annots.get(i));
            if (!(annot instanceof PDFDict)) continue;
            const subtype = annot.get(PDFName.of('Subtype'));
            const kind = typeof subtype?.asString === 'function' ? subtype.asString().slice(1) : '';
            if (kind === 'Watermark') { found.push({ index: i, kind }); continue; }
            if (kind === 'Stamp') {
                const nameValue = annot.get(PDFName.of('Name'));
                const label = typeof nameValue?.asString === 'function' ? nameValue.asString() : '';
                if (WATERMARK_WORD.test(label)) found.push({ index: i, kind });
            }
        }
    } catch { /* ignore */ }
    return found;
}

/**
 * What this PDF looks like it is watermarked with.
 *
 * @returns {Promise<{ pageCount: number, parsedPages: number,
 *   candidates: Array<{ id: string, kind: string, label: string, pages: number,
 *                       alpha: number|null, rotated: boolean }> }>}
 */
export async function detectWatermarks(file) {
    const bytes = new Uint8Array(await file.arrayBuffer());
    let pdfDoc;
    try {
        pdfDoc = await PDFDocument.load(bytes, { ignoreEncryption: true, updateMetadata: false });
    } catch {
        throw new Error('parse');
    }

    const pages = pdfDoc.getPages();
    const pageCount = pages.length;
    if (!pageCount) return { pageCount: 0, parsedPages: 0, candidates: [] };

    const flaggedOcgs = watermarkOcgNames(pdfDoc);
    const xobjectPages = new Map();
    const textPages = new Map();
    let ocPages = 0;
    let annotationPages = 0;
    let parsedPages = 0;

    for (const page of pages) {
        const flaggedProperties = flaggedPropertyNames(page, flaggedOcgs);
        const read = readPage(page, flaggedProperties);
        if (read.parsed) parsedPages += 1;
        if (read.ocSpans) ocPages += 1;
        if (watermarkAnnotations(page).length) annotationPages += 1;

        for (const [refKey, info] of read.xobjects) {
            const seen = xobjectPages.get(refKey) || {
                pages: 0, alpha: 1, rotated: false, subtype: '', names: new Set(),
            };
            for (const name of info.names) seen.names.add(name);
            xobjectPages.set(refKey, {
                pages: seen.pages + 1,
                alpha: Math.min(seen.alpha, info.alpha),
                rotated: seen.rotated || info.rotated,
                subtype: seen.subtype || info.subtype,
                names: seen.names,
            });
        }
        for (const [text, info] of read.texts) {
            const seen = textPages.get(text) || { pages: 0, alpha: 1, rotated: false };
            textPages.set(text, {
                pages: seen.pages + 1,
                alpha: Math.min(seen.alpha, info.alpha),
                rotated: seen.rotated || info.rotated,
            });
        }
    }

    const onMostPages = (count) => count >= Math.max(1, Math.ceil(pageCount * PAGE_SHARE));
    const candidates = [];

    if (ocPages) {
        candidates.push({
            id: 'oc', kind: 'oc', label: 'watermark layer', pages: ocPages, alpha: null, rotated: false,
        });
    }
    if (annotationPages) {
        candidates.push({
            id: 'annotation', kind: 'annotation', label: 'watermark annotation',
            pages: annotationPages, alpha: null, rotated: false,
        });
    }
    for (const [refKey, info] of xobjectPages) {
        // Drawn on (nearly) every page is not enough on its own — a page frame or
        // a letterhead logo does that too. It also has to be faint, turned, or
        // named like a watermark.
        if (!onMostPages(info.pages)) continue;
        const named = [...info.names].some((name) => WATERMARK_WORD.test(name));
        if (!(info.alpha < FAINT_ALPHA || info.rotated || named)) continue;
        candidates.push({
            id: `xobject:${refKey}`,
            kind: info.subtype === 'Image' ? 'image' : 'xobject',
            label: [...info.names][0] || refKey,
            pages: info.pages,
            alpha: info.alpha,
            rotated: info.rotated,
        });
    }
    for (const [text, info] of textPages) {
        if (!onMostPages(info.pages)) continue;
        if (!(info.alpha < FAINT_ALPHA || info.rotated || WATERMARK_WORD.test(text))) continue;
        candidates.push({
            id: `text:${text}`, kind: 'text', label: text,
            pages: info.pages, alpha: info.alpha, rotated: info.rotated,
        });
    }

    return { pageCount, parsedPages, candidates };
}

/**
 * Rewrite one stream without the operators that draw the chosen watermarks.
 *
 * Everything kept is copied out of the source bytes rather than re-serialised,
 * so spacing, inline images and anything this walker does not model survive.
 * Returns null when nothing matched, so the caller leaves the page alone.
 */
function stripFromStream(bytes, { properties, xobjects, texts }) {
    const tokens = tokenize(bytes);
    if (!tokens) return null;

    const out = [];
    let groupStart = 0;
    let removed = 0;
    const keepThrough = (end) => {
        if (end > groupStart) out.push(bytes.subarray(groupStart, end));
        groupStart = end;
    };
    const dropThrough = (end) => { groupStart = end; };

    const operands = [];
    let depth = 0;          // nesting of BDC/BMC inside a dropped span
    let dropUntilEMC = false;
    let textStart = null;   // byte offset of the BT that opened the current block
    let textRun = '';

    for (let i = 0; i < tokens.length; i += 1) {
        const token = tokens[i];
        if (token.type !== 'op') { operands.push(token); continue; }
        const op = token.value;
        const names = operands.filter((tk) => tk.type === 'name');
        const strings = operands.filter((tk) => tk.type === 'string');

        // Inline image data is raw bytes and must never be tokenised.
        if (op === 'ID') {
            let j = token.end + 1;
            for (;;) {
                if (j >= bytes.length - 1) return null;
                if (bytes[j] === 0x45 && bytes[j + 1] === 0x49
                    && (j === 0 || isWhite(bytes[j - 1]))
                    && (j + 2 >= bytes.length || isWhite(bytes[j + 2]) || isDelim(bytes[j + 2]))) {
                    j += 2;
                    break;
                }
                j += 1;
            }
            if (!dropUntilEMC) keepThrough(j);
            else dropThrough(j);
            operands.length = 0;
            while (i + 1 < tokens.length && tokens[i + 1].start < j) i += 1;
            continue;
        }

        if (dropUntilEMC) {
            if (op === 'BDC' || op === 'BMC') depth += 1;
            if (op === 'EMC') {
                depth -= 1;
                if (depth <= 0) { dropUntilEMC = false; dropThrough(token.end); removed += 1; }
            }
            dropThrough(token.end);
            operands.length = 0;
            continue;
        }

        switch (op) {
            case 'BDC': {
                const key = names.length > 1 ? nameOf(bytes, names[1]) : null;
                if (key && properties.has(key)) {
                    // Drop from the operand that opened this span, not from the
                    // operator — otherwise its `/OC /Name` operands stay behind.
                    keepThrough(operands[0]?.start ?? token.start);
                    dropUntilEMC = true;
                    depth = 1;
                    dropThrough(token.end);
                }
                break;
            }
            case 'Do': {
                const key = names.length ? nameOf(bytes, names[names.length - 1]) : null;
                if (key && xobjects.has(key)) {
                    keepThrough(operands[0]?.start ?? token.start);
                    dropThrough(token.end);
                    removed += 1;
                }
                break;
            }
            case 'BT': textStart = token.start; textRun = ''; break;
            case 'Tj': case "'": case '"': {
                if (strings.length) textRun += stringOf(bytes, strings[strings.length - 1]);
                break;
            }
            case 'TJ': {
                for (const tk of strings) textRun += stringOf(bytes, tk);
                break;
            }
            case 'ET': {
                // The whole BT…ET block goes, so the matrix and font it set up
                // leave with it rather than lingering over the next block.
                if (textStart != null && texts.has(normalise(textRun))) {
                    keepThrough(textStart);
                    dropThrough(token.end);
                    removed += 1;
                }
                textStart = null;
                textRun = '';
                break;
            }
            default: break;
        }
        operands.length = 0;
    }

    if (!removed) return null;
    keepThrough(bytes.length);

    let total = 0;
    for (const chunk of out) total += chunk.length;
    const merged = new Uint8Array(total);
    let at = 0;
    for (const chunk of out) { merged.set(chunk, at); at += chunk.length; }
    return { bytes: merged, removed };
}

/**
 * Take the chosen watermarks out of the PDF.
 *
 * @param {File|Blob} file
 * @param {string[]} ids candidate ids from `detectWatermarks`
 * @returns {Promise<{ blob: Blob, removed: number }>}
 */
export async function removeWatermarks(file, ids) {
    const wanted = new Set(ids || []);
    if (!wanted.size) throw new Error('nothing');

    const bytes = new Uint8Array(await file.arrayBuffer());
    let pdfDoc;
    try {
        pdfDoc = await PDFDocument.load(bytes, { ignoreEncryption: true, updateMetadata: false });
    } catch {
        throw new Error('parse');
    }

    const xobjectRefKeys = new Set();
    const texts = new Set();
    for (const id of wanted) {
        if (id.startsWith('xobject:')) xobjectRefKeys.add(id.slice('xobject:'.length));
        else if (id.startsWith('text:')) texts.add(normalise(id.slice('text:'.length)));
    }
    const dropOc = wanted.has('oc');
    const dropAnnotations = wanted.has('annotation');
    const flaggedOcgs = dropOc ? watermarkOcgNames(pdfDoc) : new Set();

    let removed = 0;
    for (const page of pdfDoc.getPages()) {
        if (dropAnnotations) {
            const marks = watermarkAnnotations(page);
            if (marks.length) {
                const annots = page.node.context.lookup(page.node.get(PDFName.of('Annots')));
                // Back to front, so removing one does not shift the next index.
                for (const mark of marks.slice().reverse()) annots.remove(mark.index);
                removed += marks.length;
            }
        }

        const properties = dropOc ? flaggedPropertyNames(page, flaggedOcgs) : new Set();
        // The chosen object may answer to a different name on each page, so the
        // names to drop are resolved per page from the references.
        const xobjects = new Set();
        if (xobjectRefKeys.size) {
            for (const [name, entry] of xobjectRefs(page)) {
                if (xobjectRefKeys.has(entry.refKey)) xobjects.add(name);
            }
        }
        if (!properties.size && !xobjects.size && !texts.size) continue;

        let streams;
        try {
            streams = contentStreamsOf(page);
        } catch {
            streams = null;
        }
        if (!streams) continue;

        const context = page.node.context;
        for (const { ref, stream } of streams) {
            let decoded;
            try {
                decoded = decodePDFRawStream(stream).decode();
            } catch {
                break; // an unsupported filter: leave this page as it is
            }
            let result;
            try {
                result = stripFromStream(decoded, { properties, xobjects, texts });
            } catch {
                result = null;
            }
            if (!result) continue;
            try {
                context.assign(ref, context.flateStream(result.bytes));
                removed += result.removed;
            } catch {
                break;
            }
        }
    }

    if (!removed) throw new Error('nothing');
    const saved = await pdfDoc.save({ useObjectStreams: false });
    return { blob: new Blob([saved], { type: 'application/pdf' }), removed };
}
